import { useState } from "react"
import { Settings, Save, RefreshCw, Database, Server, Mail, Bell } from "lucide-react"
import { Button, Card, CardBody, CardHeader, Input, Switch, Divider, Tabs, Tab, Chip } from "@heroui/react"

export default function ConfigurationsPage() {
    // System Settings
    const [systemSettings, setSystemSettings] = useState({
        appName: "Pepper Bot Admin",
        maxUploadSize: "100",
        sessionTimeout: "30",
        maintenanceMode: false,
    })

    // Database Settings
    const [dbSettings, setDbSettings] = useState({
        host: "localhost",
        port: "5432",
        database: "pepperbot",
        poolSize: "10",
        connectionTimeout: "30",
    })

    // Email Settings
    const [emailSettings, setEmailSettings] = useState({
        smtpHost: "smtp.gmail.com",
        smtpPort: "587",
        fromEmail: "admin@pepperbot.com",
        fromName: "Pepper Bot Admin",
        enableTLS: true,
    })

    // Task Processing Settings
    const [taskSettings, setTaskSettings] = useState({
        maxRetries: "3",
        retryDelay: "60",
        maxConcurrentTasks: "5",
        taskTimeout: "300",
        cleanupOldTasks: true,
        cleanupAfterDays: "30",
    })

    // Notification Settings
    const [notificationSettings, setNotificationSettings] = useState({
        emailNotifications: true,
        taskFailureAlerts: true,
        dailyReports: false,
        weeklyReports: true,
    })

    const [isSaving, setIsSaving] = useState(false)
    const [selectedTab, setSelectedTab] = useState("system")

    const handleSave = async () => {
        setIsSaving(true)
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))
        setIsSaving(false)
        // In real app, save to backend
        alert("Settings saved successfully!")
    }

    const handleReset = () => {
        if (window.confirm("Are you sure you want to reset all settings to default?")) {
            // Reset logic here
            alert("Settings reset to default")
        }
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                                <Settings className="h-5 w-5 text-primary-foreground" />
                            </div>
                            <h1 className="text-xl font-semibold text-foreground">System Configurations</h1>
                            <Chip size="sm" variant="flat" color="warning">
                                Admin Only
                            </Chip>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Button
                                color="default"
                                variant="flat"
                                onPress={handleReset}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Reset to Default</span>
                                <span className="sm:hidden">Reset</span>
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleSave}
                                isLoading={isSaving}
                                startContent={<Save className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Save Changes</span>
                                <span className="sm:hidden">Save</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="max-w-5xl mx-auto">
                    <Tabs
                        selectedKey={selectedTab}
                        onSelectionChange={(key) => setSelectedTab(key as string)}
                        aria-label="Configuration tabs"
                        color="primary"
                        variant="underlined"
                        classNames={{
                            tabList: "gap-6",
                            cursor: "w-full",
                        }}
                    >
                        {/* System Settings Tab */}
                        <Tab
                            key="system"
                            title={
                                <div className="flex items-center gap-2">
                                    <Server className="h-4 w-4" />
                                    <span>System</span>
                                </div>
                            }
                        >
                            <div className="mt-6 space-y-6">
                                <Card>
                                    <CardHeader>
                                        <h2 className="text-lg font-semibold">General System Settings</h2>
                                    </CardHeader>
                                    <Divider />
                                    <CardBody className="space-y-4">
                                        <Input
                                            label="Application Name"
                                            value={systemSettings.appName}
                                            onChange={(e) => setSystemSettings({ ...systemSettings, appName: e.target.value })}
                                        />
                                        <Input
                                            label="Max Upload Size (MB)"
                                            type="number"
                                            value={systemSettings.maxUploadSize}
                                            onChange={(e) => setSystemSettings({ ...systemSettings, maxUploadSize: e.target.value })}
                                        />
                                        <Input
                                            label="Session Timeout (minutes)"
                                            type="number"
                                            value={systemSettings.sessionTimeout}
                                            onChange={(e) => setSystemSettings({ ...systemSettings, sessionTimeout: e.target.value })}
                                        />
                                        <Switch
                                            isSelected={systemSettings.maintenanceMode}
                                            onValueChange={(value) => setSystemSettings({ ...systemSettings, maintenanceMode: value })}
                                        >
                                            <div className="flex flex-col">
                                                <span>Maintenance Mode</span>
                                                <span className="text-xs text-default-400">Prevent users from accessing the system</span>
                                            </div>
                                        </Switch>
                                    </CardBody>
                                </Card>
                            </div>
                        </Tab>

                        {/* Database Settings Tab */}
                        <Tab
                            key="database"
                            title={
                                <div className="flex items-center gap-2">
                                    <Database className="h-4 w-4" />
                                    <span>Database</span>
                                </div>
                            }
                        >
                            <div className="mt-6 space-y-6">
                                <Card>
                                    <CardHeader>
                                        <h2 className="text-lg font-semibold">Database Configuration</h2>
                                    </CardHeader>
                                    <Divider />
                                    <CardBody className="space-y-4">
                                        <Input
                                            label="Database Host"
                                            value={dbSettings.host}
                                            onChange={(e) => setDbSettings({ ...dbSettings, host: e.target.value })}
                                        />
                                        <Input
                                            label="Database Port"
                                            type="number"
                                            value={dbSettings.port}
                                            onChange={(e) => setDbSettings({ ...dbSettings, port: e.target.value })}
                                        />
                                        <Input
                                            label="Database Name"
                                            value={dbSettings.database}
                                            onChange={(e) => setDbSettings({ ...dbSettings, database: e.target.value })}
                                        />
                                        <Input
                                            label="Connection Pool Size"
                                            type="number"
                                            value={dbSettings.poolSize}
                                            onChange={(e) => setDbSettings({ ...dbSettings, poolSize: e.target.value })}
                                        />
                                        <Input
                                            label="Connection Timeout (seconds)"
                                            type="number"
                                            value={dbSettings.connectionTimeout}
                                            onChange={(e) => setDbSettings({ ...dbSettings, connectionTimeout: e.target.value })}
                                        />
                                    </CardBody>
                                </Card>
                            </div>
                        </Tab>

                        {/* Email Settings Tab */}
                        <Tab
                            key="email"
                            title={
                                <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4" />
                                    <span>Email</span>
                                </div>
                            }
                        >
                            <div className="mt-6 space-y-6">
                                <Card>
                                    <CardHeader>
                                        <h2 className="text-lg font-semibold">Email Server Configuration</h2>
                                    </CardHeader>
                                    <Divider />
                                    <CardBody className="space-y-4">
                                        <Input
                                            label="SMTP Host"
                                            value={emailSettings.smtpHost}
                                            onChange={(e) => setEmailSettings({ ...emailSettings, smtpHost: e.target.value })}
                                        />
                                        <Input
                                            label="SMTP Port"
                                            type="number"
                                            value={emailSettings.smtpPort}
                                            onChange={(e) => setEmailSettings({ ...emailSettings, smtpPort: e.target.value })}
                                        />
                                        <Input
                                            label="From Email Address"
                                            type="email"
                                            value={emailSettings.fromEmail}
                                            onChange={(e) => setEmailSettings({ ...emailSettings, fromEmail: e.target.value })}
                                        />
                                        <Input
                                            label="From Name"
                                            value={emailSettings.fromName}
                                            onChange={(e) => setEmailSettings({ ...emailSettings, fromName: e.target.value })}
                                        />
                                        <Switch
                                            isSelected={emailSettings.enableTLS}
                                            onValueChange={(value) => setEmailSettings({ ...emailSettings, enableTLS: value })}
                                        >
                                            Enable TLS/SSL
                                        </Switch>
                                    </CardBody>
                                </Card>
                            </div>
                        </Tab>

                        {/* Task Processing Tab */}
                        <Tab
                            key="tasks"
                            title={
                                <div className="flex items-center gap-2">
                                    <RefreshCw className="h-4 w-4" />
                                    <span>Task Processing</span>
                                </div>
                            }
                        >
                            <div className="mt-6 space-y-6">
                                <Card>
                                    <CardHeader>
                                        <h2 className="text-lg font-semibold">Background Task Settings</h2>
                                    </CardHeader>
                                    <Divider />
                                    <CardBody className="space-y-4">
                                        <Input
                                            label="Maximum Retries"
                                            type="number"
                                            value={taskSettings.maxRetries}
                                            onChange={(e) => setTaskSettings({ ...taskSettings, maxRetries: e.target.value })}
                                            description="Number of times to retry failed tasks"
                                        />
                                        <Input
                                            label="Retry Delay (seconds)"
                                            type="number"
                                            value={taskSettings.retryDelay}
                                            onChange={(e) => setTaskSettings({ ...taskSettings, retryDelay: e.target.value })}
                                            description="Time to wait before retrying a failed task"
                                        />
                                        <Input
                                            label="Max Concurrent Tasks"
                                            type="number"
                                            value={taskSettings.maxConcurrentTasks}
                                            onChange={(e) => setTaskSettings({ ...taskSettings, maxConcurrentTasks: e.target.value })}
                                            description="Maximum number of tasks to process simultaneously"
                                        />
                                        <Input
                                            label="Task Timeout (seconds)"
                                            type="number"
                                            value={taskSettings.taskTimeout}
                                            onChange={(e) => setTaskSettings({ ...taskSettings, taskTimeout: e.target.value })}
                                            description="Maximum time a task can run before being terminated"
                                        />
                                        <Divider />
                                        <Switch
                                            isSelected={taskSettings.cleanupOldTasks}
                                            onValueChange={(value) => setTaskSettings({ ...taskSettings, cleanupOldTasks: value })}
                                        >
                                            <div className="flex flex-col">
                                                <span>Auto-cleanup Old Tasks</span>
                                                <span className="text-xs text-default-400">Automatically delete old completed tasks</span>
                                            </div>
                                        </Switch>
                                        {taskSettings.cleanupOldTasks && (
                                            <Input
                                                label="Cleanup After (days)"
                                                type="number"
                                                value={taskSettings.cleanupAfterDays}
                                                onChange={(e) => setTaskSettings({ ...taskSettings, cleanupAfterDays: e.target.value })}
                                            />
                                        )}
                                    </CardBody>
                                </Card>
                            </div>
                        </Tab>

                        {/* Notifications Tab */}
                        <Tab
                            key="notifications"
                            title={
                                <div className="flex items-center gap-2">
                                    <Bell className="h-4 w-4" />
                                    <span>Notifications</span>
                                </div>
                            }
                        >
                            <div className="mt-6 space-y-6">
                                <Card>
                                    <CardHeader>
                                        <h2 className="text-lg font-semibold">Notification Preferences</h2>
                                    </CardHeader>
                                    <Divider />
                                    <CardBody className="space-y-4">
                                        <Switch
                                            isSelected={notificationSettings.emailNotifications}
                                            onValueChange={(value) => setNotificationSettings({ ...notificationSettings, emailNotifications: value })}
                                        >
                                            <div className="flex flex-col">
                                                <span>Email Notifications</span>
                                                <span className="text-xs text-default-400">Send notifications via email</span>
                                            </div>
                                        </Switch>
                                        <Switch
                                            isSelected={notificationSettings.taskFailureAlerts}
                                            onValueChange={(value) => setNotificationSettings({ ...notificationSettings, taskFailureAlerts: value })}
                                        >
                                            <div className="flex flex-col">
                                                <span>Task Failure Alerts</span>
                                                <span className="text-xs text-default-400">Get notified when tasks fail</span>
                                            </div>
                                        </Switch>
                                        <Switch
                                            isSelected={notificationSettings.dailyReports}
                                            onValueChange={(value) => setNotificationSettings({ ...notificationSettings, dailyReports: value })}
                                        >
                                            <div className="flex flex-col">
                                                <span>Daily Reports</span>
                                                <span className="text-xs text-default-400">Receive daily system activity reports</span>
                                            </div>
                                        </Switch>
                                        <Switch
                                            isSelected={notificationSettings.weeklyReports}
                                            onValueChange={(value) => setNotificationSettings({ ...notificationSettings, weeklyReports: value })}
                                        >
                                            <div className="flex flex-col">
                                                <span>Weekly Reports</span>
                                                <span className="text-xs text-default-400">Receive weekly system summary reports</span>
                                            </div>
                                        </Switch>
                                    </CardBody>
                                </Card>
                            </div>
                        </Tab>
                    </Tabs>
                </div>
            </main>
        </div>
    )
}
